// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import SubFolderFacets from "@store/Library/Components/CmsLibrary/SubFolderFacets";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import updateCmsLibraryParameters from "@store/Framework/Store/Store/Data/CmsLibrary/Handlers/UpdateCmsLibraryParameters";
import { CmsLibraryAssetFolderFacetModel } from "@store/Framework/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import FolderOutline from "@store/Library/Components/Icons/FolderOutline";
import FolderPlusOutline from "@store/Library/Components/Icons/FolderPlusOutline";

const mapStateToProps = (state: ApplicationState) => {
    const { currentLibrary, libraries, parameters } = state.store.data.cmsLibrary;
    const displayedLibrary = libraries[currentLibrary];

    return {
        facets: displayedLibrary?.assetFolderFacets,
        assetFolderId: parameters.assetFolderId,
        currentLibrary,
    };
};

const mapDispatchToProps = {
    updateCmsLibraryParameters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface CmsLibraryAssetFacetsStyles {
    checkbox?: CheckboxProps;
    facetWrapper?: InjectableCss;
    subFolderFacetWrapper?: InjectableCss;
}

export const cmsLibraryAssetFacetsStyles: CmsLibraryAssetFacetsStyles = {
    checkbox: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
            align-items: center;

            &:hover {
                text-decoration: underline;
            }

            span[role="checkbox"] {
                border: none;
                margin-top: 0;

                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    background: none;
                    border: 0;

                    svg {
                        color: ${getColor("primary")};
                    }
                }

                &[aria-checked="true"]:not([aria-disabled="true"]) + label {
                    color: ${getColor("primary")};
                }

                &[aria-disabled="true"] {
                    background: none;
                    border: none;
                }
                &[aria-checked="false"] svg {
                    display: block;
                }
                & + label {
                    margin-left: 10px;
                    font-size: ${x => x.theme.design.typography.size.large};
                }
                &:focus {
                    outline: none;
                    box-shadow: 0;
                }
                svg {
                    color: ${getColor("text")};
                }
            }
        `,
        iconProps: {
            size: 20,
        },
        mergeCss: true,
    },
    facetWrapper: {
        css: css`
            padding: ${pxToRem(15)};
            margin-bottom: ${pxToRem(15)};
            border: ${pxToRem(1)} solid ${getColor("common.border")};
            border-radius: ${pxToRem(14)};

            ${breakpoint("md")} {
                padding: ${pxToRem(20)};
            }
        `,
    },
    subFolderFacetWrapper: {
        css: css`
            padding-top: ${pxToRem(15)};
        `,
    },
};

const styles = cmsLibraryAssetFacetsStyles;

const CmsLibraryAssetFacets: FC<Props> = ({ facets, assetFolderId, currentLibrary, updateCmsLibraryParameters }) => {
    if (!facets) {
        return null;
    }

    const handleChange = (value: boolean, id: string) => {
        onFacetSelection(id, value);
    };

    const folderIcon = (folder: CmsLibraryAssetFolderFacetModel) => {
        return folder.subFolderFacets.length > 0 ? FolderPlusOutline : FolderOutline;
    };

    const onFacetSelection = (id: string, checked: boolean) => {
        updateCmsLibraryParameters({
            libraryType: currentLibrary,
            assetFolderId: checked ? id : "",
            page: 1,
        });
    };

    return (
        <>
            {facets.map(facet => (
                <StyledWrapper key={facet.id} {...styles.facetWrapper}>
                    <Checkbox
                        {...styles.checkbox}
                        onChange={(_, currentValue) => handleChange(currentValue, facet.id)}
                        checked={assetFolderId === facet.id}
                        iconProps={{ ...styles.checkbox?.iconProps, src: folderIcon(facet) }}
                    >
                        {facet.shortDescription}
                    </Checkbox>
                    {facet.subFolderFacets.length > 0 && (
                        <StyledWrapper {...styles.subFolderFacetWrapper}>
                            {facet.subFolderFacets.map(subFacet => (
                                <SubFolderFacets
                                    key={subFacet.id}
                                    folder={subFacet}
                                    onFacetSelection={onFacetSelection}
                                    selectedId={assetFolderId}
                                    depth={0}
                                />
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CmsLibraryAssetFacets),
    definition: {
        group: "Common",
        displayName: "CMS Library Asset Facets",
    },
};

export default widgetModule;
